import * as React from 'react';
import { matchPath, NavLink, withRouter } from 'react-router-dom';

import classNames from 'classnames';

import { PARAMS } from '@base/constants';

import { IPaginationProps, ITab } from './types';

import * as s from './Pagination.scss';

/**
 * Pagination Component
 * @return React.Node
 */

const PaginationView: React.FC<IPaginationProps> = (props: IPaginationProps) => {
	const {
		totalItems,
		limitOnPage = 6,
		pagesToShowBeforeEllipses,
		className,
		onClick,
	} = props;
	const currentPage = props.currentPage ? Number(props.currentPage) - 1 : 0;
	const pagerTabs = [];
	const pagesQuantity = Math.ceil(totalItems / limitOnPage);
	const url = matchPath(props.match.url, props.match.path.split(PARAMS.PAGE)[0]).url;
	if (pagesQuantity <= 1) {
		return null;
	}

	for (let i = 1; i <= pagesQuantity; i++) {
		pagerTabs.push(
			{
				title: i.toString(),
				url: `${url}/${i}`,
			},
		);
	}

	return (
		<div className={classNames(s.block, className)}>
			{
				pagerTabs.map((tab: ITab, index: number) => {
					let pagerClass = '';
					let classNamePrefix;
					const isBeginningOfPagination = currentPage < pagesToShowBeforeEllipses - 1;
					const isEndOfPagination = currentPage > pagesQuantity - pagesToShowBeforeEllipses;
					const isFirstPage = index === 0;
					const isLastPage = index === pagerTabs.length - 1;
					const isShouldRender =
						// всегда отображаем первую страницу
						isFirstPage ||
						// всегда отображаем последнюю страницу
						isLastPage ||
						// всегда отображаем предыдущую страницу
						index === currentPage - 1 ||
						// всегда отображаем следующую страницу
						index === currentPage + 1 ||
						// всегда отображаем ныныешнюю страницу
						index === currentPage ||
						// показываем заданное кол-во страниц, если мы в начале пагинаци
						(index < pagesToShowBeforeEllipses && isBeginningOfPagination) ||
						// показываем заданное кол-во страниц, если мы в конце пагинаци
						((index >= pagesQuantity - pagesToShowBeforeEllipses) && isEndOfPagination)
					;
					const isShowEllipsesAfter =
						!isLastPage &&
						(index + 1 !== pagesQuantity - 1) &&
						// троеточие после заданного кол-ва страниц, если начало пагинации
						(isBeginningOfPagination && index === pagesToShowBeforeEllipses - 1) ||
						// троеточие через одну страницу, если НЕ начало пагинации и не два последних элемента
						(!isBeginningOfPagination && index === currentPage + 1 && index < pagesQuantity - 2)
					;
					const isShowEllipsesBefore =
						!isFirstPage &&
						(index - 1 !== 0) &&
						// троеточие перед заданным кол-вом страниц, если конец пагинации
						(!isBeginningOfPagination && isEndOfPagination && index === pagesQuantity - pagesToShowBeforeEllipses) ||
						// троеточие через одну страницу, если НЕ начало пагинации и не два последних элемента
						(!isEndOfPagination && index === currentPage - 1 && index >= pagesToShowBeforeEllipses - 2 && index > 1)
					;

					if (!isShouldRender) {
						return null;
					}

					if (isShowEllipsesAfter) {
						classNamePrefix = s.ellipseAfter;
					}

					if (isShowEllipsesBefore) {
						classNamePrefix = s.ellipseBefore;
					}

					pagerClass = classNames(
						s.pagerPage,
						classNamePrefix,
						{ [s.activePage]: currentPage === index },
					);

					return (
						<div
							className={pagerClass}
							key={`${tab.title}_${index}`}
						>
							<NavLink
								onClick={onClick}
								className={s.pagerPageInner}
								to={{ pathname: tab.url }}
							>
								{tab.title}
							</NavLink>
						</div>
					);
				})
			}
		</div>
	);
};

export const Pagination = withRouter(PaginationView);
